import { cn } from '@/shared/lib/cn'

export interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  /** Muestra el label al lado. Si es `false`, va solo como `aria-label`. */
  showLabel?: boolean
  disabled?: boolean
  className?: string
}

/**
 * Toggle. El estado se lee por texto ademas de por posicion: un switch que solo
 * comunica con la posicion de la perilla es el caso clasico de "solo color" con
 * otro disfraz.
 *
 * **El carril encendido NO usa `--signal`.** El lima es el color de la acción
 * primaria y del foco, y un filtro no es ninguna de las dos: en el portafolio,
 * "Incluir permanentes" quedaba pintado del color más saturado de la pantalla y
 * le disputaba la mirada a "Nuevo proyecto", que es la única acción primaria
 * que hay ahí. Encendido se resuelve con el neutro de máximo contraste del
 * tema, que se distingue igual de bien y no promete una acción.
 */
export function Switch({
  checked,
  onChange,
  label,
  showLabel = true,
  disabled,
  className,
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={showLabel ? undefined : label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex items-center gap-2 text-xs whitespace-nowrap disabled:opacity-50',
        checked ? 'text-text-1' : 'text-text-2',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex h-4 w-7 shrink-0 items-center rounded-full p-0.5',
          'transition-colors duration-[var(--motion-fast)]',
          checked ? 'bg-text-1' : 'hairline bg-surface-2',
        )}
      >
        <span
          className={cn(
            'block h-3 w-3 rounded-full transition-transform duration-[var(--motion-fast)]',
            checked ? 'translate-x-3 bg-surface-1' : 'translate-x-0 bg-text-3',
          )}
        />
      </span>
      {showLabel ? <span>{label}</span> : null}
    </button>
  )
}
